import { useEffect, useId, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  show_options: {
    id: 'app.buttons.show_options',
    defaultMessage: 'Show options',
  },
});

const BRSelect = ({
  id,
  label,
  name,
  options,
  value,
  placeholder,
  onChange,
  disabled = false,
  required = false,
  feedback,
  className,
  listClassName,
}) => {
  const intl = useIntl();
  const generatedId =
    useId?.() || `br-select-${Math.random().toString(36).slice(2)}`;
  const selectId = id || generatedId;
  const buttonId = `${selectId}-toggle`;
  const radioGroupName = name || `${selectId}-options`;
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const sanitizedOptions = useMemo(() => options || [], [options]);
  const selectedOption = useMemo(
    () => sanitizedOptions.find((option) => option.value === value) || null,
    [sanitizedOptions, value],
  );

  useEffect(() => {
    if (!inputRef.current) {
      return;
    }

    inputRef.current.value = selectedOption?.label || '';
  }, [selectedOption]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (disabled || !sanitizedOptions.length) {
      setIsOpen(false);
    }
  }, [disabled, sanitizedOptions.length]);

  const handleSelectOption = (option) => {
    if (disabled) {
      return;
    }

    if (option.value === value) {
      return;
    }

    onChange?.(option.value, option);
    setIsOpen(false);
  };

  const toggleList = () => {
    if (disabled || !sanitizedOptions.length) {
      return;
    }

    setIsOpen((prev) => !prev);
  };

  const feedbackVariant = feedback?.variant || 'info';
  const feedbackRole = feedbackVariant === 'danger' ? 'alert' : 'status';

  return (
    <div
      ref={containerRef}
      className={cx('br-select', className, {
        disabled,
      })}
    >
      <div className="br-input">
        {label ? (
          <label htmlFor={selectId}>
            {label}
            {required ? ' *' : null}
          </label>
        ) : null}
        <input
          id={selectId}
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          disabled={disabled}
        />
        <button
          id={buttonId}
          className="br-button circle"
          type="button"
          aria-label={intl.formatMessage(messages.show_options)}
          tabIndex={-1}
          data-trigger="data-trigger"
          disabled={disabled || !sanitizedOptions.length}
          onClick={toggleList}
        >
          <i className="fas fa-angle-down" aria-hidden="true" />
        </button>
      </div>
      <div
        className={cx('br-list bg-pure-0', listClassName)}
        {...(isOpen ? { expanded: '' } : {})}
        style={{ zIndex: 9999 }}
      >
        {/* Opções só existem no DOM com a lista aberta: rótulos ocultos falham no axe. */}
        {isOpen &&
          sanitizedOptions.map((option, index) => {
            const optionId = `${selectId}-option-${index}`;
            const checked = selectedOption?.value === option.value;

            return (
              <div key={option.value} className="br-item" tabIndex={-1}>
                <div className="br-radio">
                  <input
                    id={optionId}
                    type="radio"
                    name={radioGroupName}
                    value={option.value}
                    checked={checked}
                    onChange={() => handleSelectOption(option)}
                    disabled={disabled}
                  />
                  <label htmlFor={optionId}>{option.label}</label>
                </div>
              </div>
            );
          })}
      </div>
      {feedback?.text ? (
        <span className={cx('feedback', feedbackVariant)} role={feedbackRole}>
          {feedback.text}
        </span>
      ) : null}
    </div>
  );
};

BRSelect.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  value: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  feedback: PropTypes.shape({
    text: PropTypes.string,
    variant: PropTypes.oneOf(['info', 'success', 'warning', 'danger']),
  }),
  className: PropTypes.string,
  listClassName: PropTypes.string,
  mode: PropTypes.oneOf(['dark', 'inverted']),
};

BRSelect.defaultProps = {
  options: [],
  value: '',
  placeholder: '',
  onChange: undefined,
  disabled: false,
  required: false,
  feedback: undefined,
  className: undefined,
  listClassName: undefined,
};

export default BRSelect;
